import React, { useEffect, useMemo, useState } from "react";
import AlumnoLayout from "@/components/AlumnoLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { es as esLocale } from "date-fns/locale/es";
import { format, parse, startOfWeek, getDay } from "date-fns";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "@/styles/big-calendar-dark.css";
import { Sparkles, Users, CalendarClock, Video, ExternalLink } from "lucide-react";

const locales = { es: esLocale };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }), getDay, locales });

// Simple palette to color per program
const PALETTE = ["#F26A21", "#0EA5E9", "#10B981", "#8B5CF6", "#F59E0B", "#EF4444"];

export default function AlumnoCalendario() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [bookingOpen, setBookingOpen] = useState(false);

  const load = () => {
    api.get("/alumno/calendar").then((r) => setSessions(r.data.items || []));
  };
  useEffect(() => { load(); }, []);

  const colorByProgram = useMemo(() => {
    const m = {};
    let i = 0;
    for (const s of sessions) {
      const key = s.program_id || "gen";
      if (!(key in m)) m[key] = PALETTE[i++ % PALETTE.length];
    }
    return m;
  }, [sessions]);

  const events = useMemo(() => sessions.map((s) => ({
    id: s.id,
    title: s.title,
    start: new Date(s.starts_at),
    end: new Date(new Date(s.starts_at).getTime() + s.duration_min * 60000),
    resource: s,
  })), [sessions]);

  const eventPropGetter = (event) => ({
    style: {
      backgroundColor: colorByProgram[event.resource.program_id || "gen"],
      borderRadius: 6,
      color: "#0B0B0B",
      fontWeight: 600,
      padding: "2px 6px",
    },
  });

  const join = async (sessionId) => {
    try {
      const { data } = await api.get(`/alumno/sessions/${sessionId}/join`);
      if (data.platform === "dcc_room" || (data.meeting_url && data.meeting_url.startsWith("/live/"))) {
        window.open(data.meeting_url, "_blank");
      } else {
        window.open(data.meeting_url, "_blank");
      }
    } catch (e) {
      if (e.response?.status === 423) {
        toast.error("La sesión aún no está disponible o ya terminó.");
      } else {
        toast.error("No autorizado o sesión inexistente.");
      }
    }
  };

  return (
    <AlumnoLayout title="Mi calendario">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Fase 9 · Calendario</p>
          <h1 className="mt-1 text-3xl font-bold text-white font-display">Mi calendario</h1>
          <p className="text-sm text-gray-400 mt-1">Sesiones live, mentorías reservadas y programas activos.</p>
        </div>
        <button onClick={() => setBookingOpen(true)}
          data-testid="alu-book-mentoria-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold text-sm min-h-[44px]">
          <Sparkles className="w-4 h-4" /> Agendar mentoría 1:1
        </button>
      </div>

      <div className="p-3 rounded-2xl bg-[#111827] border border-white/10" data-testid="alu-calendar-container">
        <Calendar
          localizer={localizer}
          events={events}
          defaultView="week"
          views={["month", "week", "day"]}
          step={30}
          timeslots={2}
          min={new Date(2020, 0, 1, 7, 0)}
          max={new Date(2020, 0, 1, 22, 0)}
          style={{ height: 600 }}
          culture="es"
          eventPropGetter={eventPropGetter}
          onSelectEvent={(e) => join(e.resource.id)}
          messages={{
            next: "Sig.", previous: "Ant.", today: "Hoy",
            month: "Mes", week: "Semana", day: "Día",
            date: "Fecha", time: "Hora", event: "Sesión",
            noEventsInRange: "Sin sesiones en este rango",
            allDay: "Todo el día", showMore: (n) => `+${n} más`,
          }}
        />
      </div>

      {bookingOpen && <BookMentoriaModal onClose={() => setBookingOpen(false)} onDone={() => { setBookingOpen(false); load(); }} />}
    </AlumnoLayout>
  );
}

function BookMentoriaModal({ onClose, onDone }) {
  const [instructors, setInstructors] = useState([]);
  const [instructorId, setInstructorId] = useState("");
  const [date, setDate] = useState(new Date(Date.now() + 86400_000).toISOString().slice(0, 10));
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Grab instructors via public team endpoint (best-effort)
    api.get("/team", { params: { role: "instructor" } })
      .then((r) => setInstructors(r.data.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!instructorId || !date) return setSlots([]);
    api.get(`/alumno/instructors/${instructorId}/availability`, { params: { date } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]));
  }, [instructorId, date]);

  const submit = async () => {
    if (!selectedSlot) return toast.error("Elige un slot disponible");
    setBusy(true);
    try {
      await api.post("/alumno/bookings", {
        instructor_id: instructorId,
        slot_starts_at: selectedSlot.starts_at,
        slot_duration_min: selectedSlot.duration_min,
        topic: topic || undefined,
      });
      toast.success("Mentoría reservada · te llegará recordatorio 24h antes");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo reservar");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="book-mentoria-modal"
        className="w-full max-w-lg bg-[#111827] border border-white/10 rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-white font-display">Agendar mentoría 1:1</h2>
        <p className="text-xs text-gray-500 mt-1">Elige instructor, día y hora. 30 minutos por slot.</p>
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Instructor</label>
            <select value={instructorId} onChange={(e) => setInstructorId(e.target.value)}
              data-testid="bm-instructor-select"
              className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white">
              <option value="">— Elige —</option>
              {instructors.map((i) => <option key={i.user_id || i.id} value={i.user_id || i.id}>{i.name || i.full_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Fecha</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              data-testid="bm-date-input"
              className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
          </div>
          {instructorId && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Slots disponibles</label>
              <div className="mt-2 grid grid-cols-3 gap-2 max-h-40 overflow-y-auto" data-testid="bm-slots-grid">
                {slots.map((s) => (
                  <button key={s.starts_at}
                    onClick={() => setSelectedSlot(s)}
                    data-testid={`bm-slot-${s.starts_at.slice(11, 16)}`}
                    className={`text-xs py-2 rounded-md min-h-[44px] ${
                      selectedSlot?.starts_at === s.starts_at
                        ? "bg-[#F26A21] text-[#0B0B0B] font-bold"
                        : "bg-white/5 text-white hover:bg-white/10"
                    }`}>
                    {new Date(s.starts_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
                  </button>
                ))}
                {slots.length === 0 && <p className="col-span-3 text-xs text-gray-500 text-center py-4">Sin slots ese día.</p>}
              </div>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Tema (opcional)</label>
            <textarea value={topic} onChange={(e) => setTopic(e.target.value)} rows={2}
              data-testid="bm-topic-input"
              className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-white/10 text-gray-300 text-sm min-h-[44px]">Cancelar</button>
          <button onClick={submit} disabled={busy || !selectedSlot}
            data-testid="bm-submit-btn"
            className="px-6 py-2 rounded-md bg-[#F26A21] hover:bg-[#D9541A] disabled:opacity-50 text-[#0B0B0B] font-semibold text-sm min-h-[44px]">
            {busy ? "Reservando…" : "Confirmar reserva"}
          </button>
        </div>
      </div>
    </div>
  );
}
